import Cookies from 'js-cookie'

// UTM capture for the Energywell UTM Tagging Standard (GX-7127, legacy-cart
// counterpart of GX-7093). Rules match the Proton carts field-for-field:
// a block is only ever captured or sent whole — all four params present as
// non-empty strings (after trim) of at most 255 chars — otherwise the whole
// block is rejected. Partial blocks never overwrite or blank a stored block;
// the backend enforces the same rules atomically on `enrollment.utm`.

export const UTM_KEYS = ['utm_campaign', 'utm_source', 'utm_medium', 'utm_content']

export const SALES_CHANNEL_COOKIE_NAME = '_ewchannel'

const MAX_UTM_VALUE_LENGTH = 255

// The cookie is persisted via btoa, which throws on any code point above
// 0xFF — values that can't survive that round trip are rejected up front so
// a unicode UTM param can never break the cookie write.
const NON_LATIN_1 = /[^ -ÿ]/

// Validates a candidate UTM block (parsed query params or a block read back
// from the cookie). Returns a trimmed four-field block, or null when any rule
// fails. Type-checked so a tampered cookie can't dodge the rules.
export const sanitizeUTMBlock = candidate => {
  if (!candidate || typeof candidate !== 'object') return null
  const block = {}
  for (const key of UTM_KEYS) {
    const value = candidate[key]
    if (typeof value !== 'string') return null
    const trimmed = value.trim()
    if (trimmed.length === 0 || trimmed.length > MAX_UTM_VALUE_LENGTH) return null
    if (NON_LATIN_1.test(trimmed)) return null
    block[key] = trimmed
  }
  return block
}

// Merges new channel/UTM data over the existing cookie payload so a sales
// channel write never drops the UTM block and vice versa.
export const mergeChannelData = (saved, salesChannel, utm) => ({
  ...(saved && typeof saved === 'object' ? saved : {}),
  ...(salesChannel ? { salesChannel } : {}),
  ...(utm ? { utm } : {}),
})

// Reads and decodes the sales-channel cookie payload. Never throws: an
// absent, corrupted, or non-object cookie yields {} so a bad cookie can
// never block the write path from repairing it.
export const readChannelCookieData = () => {
  try {
    const channelCookie = Cookies.get(SALES_CHANNEL_COOKIE_NAME)
    if (!channelCookie) return {}
    const parsed = JSON.parse(atob(channelCookie))
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (err) {
    return {}
  }
}

// Reads the UTM block stored in the sales-channel cookie, re-validated on
// read. Returns null when the cookie is absent, unreadable, or holds no
// complete block.
export const getStoredUTMBlock = () => sanitizeUTMBlock(readChannelCookieData().utm)
